/**
 * 登录页面 - 支持 Casdoor OAuth / 用户名密码登录
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { UserIcon, LockClosedIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { authApi } from '../services/api';

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'password' | 'casdoor'>('password');
  const [casdoorEnabled, setCasdoorEnabled] = useState(false);
  const [casdoorAuthUrl, setCasdoorAuthUrl] = useState('');

  // 加载 Casdoor OAuth 配置
  useEffect(() => {
    authApi.getCasdoorConfig()
      .then((res) => {
        if (res.data.enabled) {
          setCasdoorEnabled(true);
          setCasdoorAuthUrl(res.data.auth_url);
        }
      })
      .catch(() => {
        // 获取配置失败，Casdoor 不可用
      });

    // 从 URL 中提取推荐码并暂存，供统一认证回调使用
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      sessionStorage.setItem('referral_ref', refCode);
    }
  }, []);

  const handleCasdoorLogin = () => {
    if (casdoorAuthUrl) {
      window.location.href = casdoorAuthUrl;
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('请输入用户名和密码');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await login(username.trim(), password);
      if (!result.success) {
        setError(result.message);
      }
    } catch {
      setError('登录失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="w-full max-w-md mx-4">
        {/* Logo 区域 */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-600 text-white text-2xl font-bold mb-4 shadow-lg">
            夺
          </div>
          <h1 className="text-2xl font-bold text-gray-900">AI 智能体--workeasy</h1>
          <p className="mt-1 text-sm text-gray-500">智能标书生成与管理平台</p>
        </div>

        {/* 登录卡片 */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* 切换登录方式 — 仅当 Casdoor 可用时显示统一认证 tab */}
          <div className="flex border border-gray-200 rounded-lg p-1 mb-6">
            <button
              onClick={() => { setMode('password'); setError(null); }}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                mode === 'password' || !casdoorEnabled
                  ? 'bg-blue-600 text-white shadow'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              账号登录
            </button>
            {casdoorEnabled && (
              <button
                onClick={() => { setMode('casdoor'); setError(null); }}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                  mode === 'casdoor'
                    ? 'bg-blue-600 text-white shadow'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                统一认证登录
              </button>
            )}
          </div>

          {mode === 'password' ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">用户名</label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="请输入用户名"
                    autoFocus
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">密码</label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="请输入密码"
                  />
                </div>
              </div>

              {error && (
                <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60 transition-colors"
              >
                {loading ? '登录中...' : '登录'}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="text-center py-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 text-blue-600 mb-3">
                  <ShieldCheckIcon className="w-8 h-8" />
                </div>
                <p className="text-sm text-gray-700 font-medium">统一认证登录</p>
                <p className="text-xs text-gray-400 mt-1">使用 Casdoor 统一身份认证快速登录</p>
              </div>

              {error && (
                <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                  {error}
                </div>
              )}

              {casdoorEnabled ? (
                <button
                  type="button"
                  onClick={handleCasdoorLogin}
                  className="w-full py-2.5 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  前往统一认证
                </button>
              ) : (
                <p className="text-center text-sm text-gray-400 py-4">
                  统一认证服务暂不可用，请使用账号登录
                </p>
              )}
            </div>
          )}
        </div>

        {/* 版权信息 */}
        <p className="text-center mt-4 text-xs text-gray-400">
          欢迎访问 AI 智能体--workeasy，版权：北京法华科技有限公司
          <br />
          网站备案号：<a href="https://beian.miit.gov.cn/" target="_blank" className="text-gray-400 hover:text-gray-600">京ICP备2022005503号-1</a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
